/**
 * Raketech Final Report v2
 *
 * Rebuilds the underpayment claim on top of Raketech's replacement workbook
 * (Mar 2023 – May 2026) while keeping v1's evidence hierarchy intact. The
 * original report at /raketech/final-report is untouched and remains the record
 * of the claim as it stood before their new numbers arrived.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  Calendar,
  Database,
  Download,
  FileText,
  Loader2,
  RefreshCw,
  Scale,
  Shield,
  Sigma,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  buildClaim,
  buildClaimBridge,
  RECON_END,
  type CompareResponse,
} from '../lib/raketechFinalReportV2';
import {
  CLAIM_SOURCE_LABELS,
  type ClaimBridgeStep,
  type ClaimRow,
  type ClaimSource,
} from '../types/raketechFinalReportV2.types';
import type { NewReconDataset } from '../types/raketechReconDiff.types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) return JSON.parse(auth).state?.token || null;
  } catch { /* ignore */ }
  return null;
};

/**
 * v2 reaches Dec 2024 onward from Raketech's own columns, so it does not need an
 * Affiliation Cloud session the way v1 does. If the user happens to already hold
 * a valid token from another page we use it for a free cross-check, but nothing
 * here prompts for one.
 */
const getExistingACToken = (): string | null => {
  try {
    const stored = localStorage.getItem('affiliation-cloud-token');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) return parsed.token;
    }
  } catch { /* ignore */ }
  return null;
};

interface PostReconResponse {
  rows?: {
    reconAccount?: { accountId?: string };
    acReportCommission?: number;
    acMonthly?: { month: string; commission: number }[];
  }[];
}

const fmtInt = (v: number) => v.toLocaleString('en-US');

const fmtEUR = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

const fmtEURExact = (v: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(v);

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m: string) => {
  if (!/^\d{4}-\d{2}$/.test(m)) return m;
  const [y, mo] = m.split('-');
  return `${MONTH_SHORT[parseInt(mo, 10) - 1] || mo} ${y}`;
};

const SOURCE_STYLES: Record<ClaimSource, string> = {
  reconciliation: 'bg-zinc-500/10 text-zinc-300 border-zinc-500/20',
  synced: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  manual_check: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  raketech_restated: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

const SOURCE_SHORT: Record<ClaimSource, string> = {
  reconciliation: 'Old recon',
  synced: 'Synced',
  manual_check: 'Manual',
  raketech_restated: 'Their restatement',
};

type MainTab = 'underpaid' | 'overpaid' | 'all' | 'bridge' | 'sources' | 'unverifiable';

/** The three tabs that share the account table, and how each selects rows. */
const ROW_TABS = {
  underpaid: {
    label: 'Underpaid',
    /** Negative means they invoiced less than the account earned. */
    match: (r: ClaimRow) => r.underpaid < -1,
    sort: (a: ClaimRow, b: ClaimRow) => a.underpaid - b.underpaid,
    tone: 'red' as const,
  },
  overpaid: {
    label: 'Overpaid',
    match: (r: ClaimRow) => r.underpaid > 1,
    sort: (a: ClaimRow, b: ClaimRow) => b.underpaid - a.underpaid,
    tone: 'amber' as const,
  },
  all: {
    label: 'All accounts',
    match: () => true,
    sort: (a: ClaimRow, b: ClaimRow) => Math.abs(b.underpaid) - Math.abs(a.underpaid),
    tone: 'zinc' as const,
  },
};

const isRowTab = (t: MainTab): t is keyof typeof ROW_TABS => t in ROW_TABS;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const RaketechFinalReportV2: React.FC = () => {
  const [dataset, setDataset] = useState<NewReconDataset | null>(null);
  const [compare, setCompare] = useState<CompareResponse | null>(null);
  const [postRecon, setPostRecon] = useState<PostReconResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [applyMargin, setApplyMargin] = useState(true);
  const [lastMonth, setLastMonth] = useState('2026-05');
  const [activeTab, setActiveTab] = useState<MainTab>('underpaid');
  const [searchTerm, setSearchTerm] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [onlyRestatement, setOnlyRestatement] = useState(false);

  const load = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { setError('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const [dsResp, cmpResp] = await Promise.all([
        fetch('/api/raketech/recon-new', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/raketech/compare?invoicePeriod=all', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const dsJson = await dsResp.json() as { success: boolean; data?: NewReconDataset; error?: string };
      const cmpJson = await cmpResp.json() as { success: boolean; data?: CompareResponse & { empty?: boolean; message?: string }; error?: string };

      if (!dsJson.success || !dsJson.data) throw new Error(dsJson.error || 'Failed to load the new reconciliation');
      if (!cmpJson.success || !cmpJson.data) throw new Error(cmpJson.error || 'Failed to load the comparison');
      if (cmpJson.data.empty) throw new Error(cmpJson.data.message || 'No reconciliation data imported yet');

      setDataset(dsJson.data);
      setCompare(cmpJson.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Kept separate from `load` so changing the month does not re-run the
  // expensive comparison query.
  const loadAcCrossCheck = useCallback(async () => {
    const token = getAuthToken();
    const acToken = getExistingACToken();
    if (!token || !acToken) { setPostRecon(null); return; }
    try {
      const resp = await fetch(`/api/raketech/post-recon?lastMonth=${lastMonth}`, {
        headers: { Authorization: `Bearer ${token}`, 'X-Affiliation-Cloud-Token': acToken },
      });
      const json = await resp.json() as { success: boolean; data?: PostReconResponse };
      setPostRecon(json.success && json.data ? json.data : null);
    } catch {
      setPostRecon(null);
    }
  }, [lastMonth]);

  useEffect(() => { loadAcCrossCheck(); }, [loadAcCrossCheck]);

  const acMonthlyByAccountId = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    for (const row of postRecon?.rows || []) {
      const id = String(row.reconAccount?.accountId || '').trim();
      if (!id) continue;
      const months = map.get(id) || new Map<string, number>();
      for (const m of row.acMonthly || []) {
        months.set(m.month, (months.get(m.month) || 0) + (m.commission || 0));
      }
      map.set(id, months);
    }
    return map;
  }, [postRecon]);

  const claim = useMemo(() => {
    if (!dataset || !compare) return null;
    return buildClaim(compare, dataset, {
      lastMonth,
      applyMargin,
      acMonthlyByAccountId: acMonthlyByAccountId.size > 0 ? acMonthlyByAccountId : undefined,
    });
  }, [dataset, compare, lastMonth, applyMargin, acMonthlyByAccountId]);

  const bridge = useMemo<ClaimBridgeStep[]>(() => {
    if (!dataset || !claim) return [];
    return buildClaimBridge(dataset, claim.summary);
  }, [dataset, claim]);

  /** Counts for the tab labels, before search narrows anything down. */
  const tabCounts = useMemo(() => {
    const counts = { underpaid: 0, overpaid: 0, all: 0 };
    for (const r of claim?.rows || []) {
      counts.all += 1;
      if (ROW_TABS.underpaid.match(r)) counts.underpaid += 1;
      if (ROW_TABS.overpaid.match(r)) counts.overpaid += 1;
    }
    return counts;
  }, [claim]);

  const rowTab = isRowTab(activeTab) ? activeTab : 'underpaid';

  const claimRows = useMemo(() => {
    if (!claim) return [];
    const spec = ROW_TABS[rowTab];
    let rows = claim.rows.filter(spec.match);
    if (onlyRestatement) rows = rows.filter((r) => r.attributableToRestatement > 1);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      rows = rows.filter((r) =>
        r.partner.toLowerCase().includes(q)
        || r.username.toLowerCase().includes(q)
        || r.accountId.includes(q)
        || r.acIds.some((id) => id.includes(q))
        || r.brands.some((b) => b.toLowerCase().includes(q)));
    }
    return [...rows].sort(spec.sort);
  }, [claim, rowTab, onlyRestatement, searchTerm]);

  const claimTotals = useMemo(() => {
    let invoiced = 0, earned = 0, underpaid = 0, restatement = 0;
    for (const r of claimRows) {
      invoiced += r.invoicedTotal;
      earned += r.earnedTotal;
      underpaid += r.underpaid;
      restatement += r.attributableToRestatement;
    }
    return { invoiced, earned, underpaid, restatement, count: claimRows.length };
  }, [claimRows]);

  const toggleRow = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const exportCsv = () => {
    if (claimRows.length === 0) { toast.error('Nothing to export'); return; }
    const header = [
      'Account ID', 'AC IDs', 'Partner', 'Username', 'Brands', 'Geo', 'Evidence source',
      'Invoiced Mar23-Nov24', 'Invoiced Dec24+', 'Invoiced total',
      // Every candidate, so the export shows what was rejected as well as chosen.
      'Old recon', 'Synced -5%', 'Manual -5%', 'Their restated',
      'Earned Mar23-Nov24', 'Earned Dec24+', 'AC Dec24+ (reference)', 'Earned total',
      'Difference', 'Difference %', 'Of which from their restatement',
      'New to this report', 'Verifiable in adwstats',
    ];
    const candidate = (r: ClaimRow, source: ClaimSource) => {
      const c = r.candidates.find((x) => x.source === source);
      return c && c.available ? c.amount : '';
    };
    const lines = claimRows.map((r) => [
      r.accountId, r.acIds.join(' '), r.partner, r.username, r.brands.join(' / '), r.geos.join(' '),
      CLAIM_SOURCE_LABELS[r.chosenSource],
      r.invoicedRecon, r.invoicedPostRecon, r.invoicedTotal,
      candidate(r, 'reconciliation'), candidate(r, 'synced'),
      candidate(r, 'manual_check'), candidate(r, 'raketech_restated'),
      r.earnedReconPeriod, r.earnedTail, r.earnedTailFromAc, r.earnedTotal,
      r.underpaid, r.underpaidPct, r.attributableToRestatement,
      r.newToThisReport ? 'yes' : 'no', r.missingFromAdwstats ? 'no' : 'yes',
    ]);
    const csv = [header, ...lines]
      .map((row) => row.map((cell) => {
        const s = String(cell ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `raketech-claim-v2-${rowTab}-${lastMonth}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${claimRows.length} accounts`);
  };

  // -------------------------------------------------------------------------
  if (isLoading && !claim) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-zinc-400">Rebuilding the claim on their new numbers…</p>
        </div>
      </div>
    );
  }

  if (error && !claim) {
    return (
      <div className="min-h-screen bg-[hsl(225,15%,6%)] p-8">
        <div className="max-w-xl mx-auto bg-red-500/5 border border-red-500/20 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-sm font-semibold text-white mb-1">Could not build the report</h2>
              <p className="text-xs text-zinc-400 mb-4">{error}</p>
              <button
                onClick={load}
                className="px-3 py-1.5 text-xs bg-[hsl(225,15%,14%)] hover:bg-[hsl(225,15%,18%)] text-zinc-200 rounded-lg border border-[hsl(225,15%,22%)]"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!claim || !dataset) return null;

  const { summary } = claim;
  const controls = dataset.controls;
  const theirClaim = Math.abs(controls.underpaymentAfterFees ?? 0);

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl font-semibold text-white">Final Report v2</h1>
              <span className="px-2 py-0.5 text-[10px] rounded border border-amber-500/30 bg-amber-500/10 text-amber-400">
                Their new reconciliation
              </span>
              {acMonthlyByAccountId.size > 0 && (
                <span
                  className="px-2 py-0.5 text-[10px] rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                  title={`Cross-checking ${acMonthlyByAccountId.size} accounts against Affiliation Cloud`}
                >
                  AC cross-check on
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 max-w-3xl leading-relaxed">
              The claim rebuilt with Raketech's replacement workbook added as a fourth evidence
              source. Each account is valued at the strongest figure available — their original
              reconciliation, what we synced from the operator, a manual backend check, or their
              own restated number — and compared to what they invoiced. Where their restatement is
              the strongest figure, the shortfall is proved by their own admission.
              v1 remains untouched at <span className="text-zinc-400">/raketech/final-report</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <Calendar className="h-3.5 w-3.5 text-zinc-500" />
              <span className="text-zinc-500">Through</span>
              <select
                value={lastMonth}
                onChange={(e) => setLastMonth(e.target.value)}
                className="bg-transparent text-zinc-200 outline-none cursor-pointer"
              >
                {dataset.months.map((m) => (
                  <option key={m} value={m} className="bg-[hsl(225,15%,12%)]">{fmtMonth(m)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={applyMargin}
                onChange={(e) => setApplyMargin(e.target.checked)}
                className="accent-amber-500"
              />
              Net their 5% margin
            </label>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
            <button
              onClick={load}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-300 hover:text-white bg-[hsl(225,15%,12%)] hover:bg-[hsl(225,15%,16%)] border border-[hsl(225,15%,20%)] rounded-lg transition-colors"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Headline */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <StatCard
          label="They admit"
          value={fmtEUR(theirClaim)}
          hint="Their own bottom line"
          icon={<Scale className="h-4 w-4" />}
          color="zinc"
        />
        <StatCard
          label={rowTab === 'overpaid' ? 'Overpaid to them' : rowTab === 'all' ? 'Net position' : 'This report claims'}
          value={fmtEUR(Math.abs(claimTotals.underpaid))}
          hint={`${claimTotals.count} ${rowTab === 'all' ? 'accounts, netted' : `${rowTab} accounts`}`}
          icon={<AlertTriangle className="h-4 w-4" />}
          color={ROW_TABS[rowTab].tone === 'zinc' ? 'zinc' : ROW_TABS[rowTab].tone}
        />
        <StatCard
          label="Proved by their restatement"
          value={fmtEUR(claimTotals.restatement)}
          hint="Shortfall from their own new figures"
          icon={<FileText className="h-4 w-4" />}
          color="amber"
        />
        <StatCard
          label="Earned, claimed accounts"
          value={fmtEUR(claimTotals.earned)}
          hint="Best evidence per account"
          icon={<Sigma className="h-4 w-4" />}
          color="emerald"
        />
        <StatCard
          label="Invoiced, same accounts"
          value={fmtEUR(claimTotals.invoiced)}
          hint="What they billed"
          icon={<Database className="h-4 w-4" />}
          color="cyan"
        />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-1.5 mb-4 border-b border-[hsl(225,15%,15%)] pb-3">
        {([
          ['underpaid', `Underpaid (${tabCounts.underpaid})`],
          ['overpaid', `Overpaid (${tabCounts.overpaid})`],
          ['all', `All accounts (${tabCounts.all})`],
          ['bridge', 'Their number vs ours'],
          ['sources', 'Evidence used'],
          ['unverifiable', `Unverifiable (${summary.accountsMissingFromAdwstats})`],
        ] as [MainTab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors ${
              activeTab === key
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/25'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-[hsl(225,15%,12%)] border border-transparent'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* The claim */}
      {/* ------------------------------------------------------------------ */}
      {isRowTab(activeTab) && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            {rowTab === 'underpaid' && (
              <>
                One row per underpaid account, largest shortfall first. Earnings up to {fmtMonth(RECON_END)} come
                from whichever of the four sources is highest; from {fmtMonth('2024-12')} onward they come from
                Raketech's own monthly columns, which is why this report no longer needs a live Affiliation
                Cloud session to reach the recent period. Accounts they overpaid are on their own tab rather
                than being allowed to offset here — netting them off is the main reason their bottom line is
                smaller than this one.
              </>
            )}
            {rowTab === 'overpaid' && (
              <>
                Accounts where Raketech invoiced more than we can evidence was earned. These are shown for
                completeness and are deliberately kept out of the claim: offsetting them against underpaid
                accounts is precisely the step that shrinks their bottom line to {fmtEURExact(theirClaim)}. An
                account can also land here simply because we hold no data for it, so treat this as a list to
                review rather than a debt.
              </>
            )}
            {rowTab === 'all' && (
              <>
                Every account in either report, largest absolute difference first, with underpaid and overpaid
                netted against each other. This is the closest view to how Raketech present their own figure,
                and the gap between this net total and the underpaid tab is the value of their netting.
              </>
            )}
          </Explainer>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Filter by partner, brand, account id…"
              className="px-3 py-1.5 text-xs bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg text-zinc-200 placeholder:text-zinc-600 outline-none focus:border-amber-500/40 w-72"
            />
            <label className="flex items-center gap-2 px-3 py-1.5 text-xs text-zinc-300 bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={onlyRestatement}
                onChange={(e) => setOnlyRestatement(e.target.checked)}
                className="accent-amber-500"
              />
              Only accounts their restatement exposes
            </label>
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="w-6" />
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner / Brands</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Evidence</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Invoiced</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Earned</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Dec 24 →</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">
                      {rowTab === 'underpaid' ? 'Shortfall' : 'Difference'}
                    </th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">%</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">From restatement</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {claimRows.slice(0, 400).map((row) => (
                    <React.Fragment key={row.key}>
                      <tr
                        className="hover:bg-[hsl(225,15%,12%)] cursor-pointer"
                        onClick={() => toggleRow(row.key)}
                      >
                        <td className="px-2 py-2 text-zinc-600">
                          {expanded.has(row.key)
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />}
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-zinc-200 truncate max-w-[200px]">{row.partner || '—'}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[200px]">
                            {row.brands.slice(0, 3).join(', ') || '—'}
                            {row.brands.length > 3 && ` +${row.brands.length - 3}`}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-[10px] text-zinc-400">
                            {row.accountId || '—'}
                            {row.newToThisReport && (
                              <span className="text-amber-400/80"> · new to v2</span>
                            )}
                          </div>
                          <div className="text-[10px] text-zinc-600">
                            {row.acIds.length > 0 ? `AC ${row.acIds.join(', ')}` : 'no AC account'}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${SOURCE_STYLES[row.chosenSource]}`}>
                            {SOURCE_SHORT[row.chosenSource]}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(row.earnedTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-purple-400/80">
                          {row.earnedTail ? fmtEUR(row.earnedTail) : '—'}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-medium ${
                          row.underpaid < -1 ? 'text-red-400'
                            : row.underpaid > 1 ? 'text-amber-400' : 'text-zinc-500'
                        }`}>
                          {fmtEUR(row.underpaid)}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums text-[10px] ${
                          row.underpaid < -1 ? 'text-red-500/80'
                            : row.underpaid > 1 ? 'text-amber-500/80' : 'text-zinc-600'
                        }`}>
                          {row.underpaidPct.toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-400/90">
                          {row.attributableToRestatement > 1 ? fmtEUR(row.attributableToRestatement) : '—'}
                        </td>
                      </tr>

                      {expanded.has(row.key) && (
                        <tr className="bg-[hsl(225,15%,8%)]">
                          <td />
                          <td colSpan={9} className="px-3 py-3">
                            <ClaimDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {claimRows.length > 400 && (
              <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-[hsl(225,15%,15%)]">
                Showing the largest 400 of {claimRows.length}. Export the CSV for the full list.
              </div>
            )}
            {claimRows.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500">
                No {rowTab === 'all' ? '' : `${rowTab} `}accounts match this filter.
              </div>
            )}
            {claimRows.length > 0 && (
              <div className="px-4 py-3 border-t border-[hsl(225,15%,15%)] flex flex-wrap items-center justify-between gap-3 text-xs">
                <span className="text-zinc-500">
                  <strong className="text-white">{claimTotals.count}</strong>
                  {' '}{rowTab === 'all' ? 'accounts' : `${rowTab} accounts`} through {fmtMonth(lastMonth)}
                </span>
                <div className="flex flex-wrap items-center gap-4">
                  <span className="text-zinc-500">Earned <span className="text-emerald-400 tabular-nums">{fmtEUR(claimTotals.earned)}</span></span>
                  <span className="text-zinc-500">Invoiced <span className="text-zinc-300 tabular-nums">{fmtEUR(claimTotals.invoiced)}</span></span>
                  <span className="text-zinc-500">
                    {rowTab === 'underpaid' ? 'Shortfall' : rowTab === 'overpaid' ? 'Overpaid' : 'Net'}
                    {' '}
                    <span className={`tabular-nums font-bold ${
                      claimTotals.underpaid < 0 ? 'text-red-400' : 'text-amber-400'
                    }`}>
                      {fmtEUR(claimTotals.underpaid)}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Bridge */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'bridge' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Their report concludes they owe {fmtEURExact(theirClaim)}. This report concludes
            {' '}{fmtEURExact(Math.abs(claimTotals.underpaid))}. The gap is not a disagreement about what was
            earned — it comes from how the two documents are built. Theirs nets one grand total against
            another, so accounts they overpaid silently cancel accounts they underpaid, and it then
            deducts transaction fees their own ledger does not support. This one measures each account
            separately and adds nothing back.
          </Explainer>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden divide-y divide-[hsl(225,15%,14%)]">
            {bridge.map((step, i) => <BridgeRow key={i} step={step} />)}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Evidence used */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'sources' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Which source won for each account. The hierarchy is deliberately "highest wins": we take
            the strongest evidence of what was actually earned, whether that is ours or theirs. Rows
            decided by their own restatement are the most defensible, because disputing them means
            disputing their own report.
          </Explainer>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(Object.keys(CLAIM_SOURCE_LABELS) as ClaimSource[]).map((source) => {
              const b = summary.bySource[source];
              return (
                <div key={source} className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
                  <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium mb-3 ${SOURCE_STYLES[source]}`}>
                    {SOURCE_SHORT[source]}
                  </span>
                  <p className="text-[11px] text-zinc-500 mb-3 leading-relaxed">{CLAIM_SOURCE_LABELS[source]}</p>
                  <dl className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Accounts</dt>
                      <dd className="text-zinc-200 tabular-nums">{b.accounts}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Earned</dt>
                      <dd className="text-emerald-400 tabular-nums">{fmtEUR(b.earned)}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-zinc-500">Net position</dt>
                      <dd className={`tabular-nums ${b.underpaid < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                        {fmtEUR(b.underpaid)}
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })}
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
            <h3 className="text-sm font-semibold text-white mb-3">What their restatement changes</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div>
                <p className="text-zinc-500 mb-1">Claim without their new figures</p>
                <p className="text-lg text-zinc-300 tabular-nums">{fmtEUR(Math.abs(summary.underpaidWithoutRestatement))}</p>
                <p className="text-[10px] text-zinc-600 mt-1">v1's evidence only, same period</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Claim with them</p>
                <p className="text-lg text-red-400 tabular-nums font-semibold">{fmtEUR(Math.abs(summary.underpaidTotal))}</p>
                <p className="text-[10px] text-zinc-600 mt-1">All four sources, highest wins</p>
              </div>
              <div>
                <p className="text-zinc-500 mb-1">Added by their own admission</p>
                <p className="text-lg text-amber-400 tabular-nums font-semibold">{fmtEUR(summary.restatementEffect)}</p>
                <p className="text-[10px] text-zinc-600 mt-1">
                  {summary.accountsNewToReport} accounts appear only in their new report
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Unverifiable */}
      {/* ------------------------------------------------------------------ */}
      {activeTab === 'unverifiable' && (
        <div className="space-y-4 animate-fade-in">
          <Explainer>
            Accounts with no matching adwstats account, so the only figures available are Raketech's
            own. They are still claimable — the numbers are their admission — but we hold no
            independent evidence if they later revise them again, so they are worth separating before
            the claim goes out.
          </Explainer>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard
              label="Accounts we cannot verify"
              value={String(summary.accountsMissingFromAdwstats)}
              hint={`of ${summary.accounts} in the report`}
              icon={<Shield className="h-4 w-4" />}
              color="amber"
            />
            <StatCard
              label="Earnings resting on their word"
              value={fmtEUR(summary.unverifiableEarnings)}
              hint="No independent support"
              icon={<AlertTriangle className="h-4 w-4" />}
              color="red"
            />
            <StatCard
              label="Shortfall from those accounts"
              value={fmtEUR(summary.unverifiableUnderpaid)}
              hint="Part of the claim above"
              icon={<ArrowDownRight className="h-4 w-4" />}
              color="zinc"
            />
          </div>

          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)]">
                  <tr>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Partner / Brands</th>
                    <th className="text-left px-3 py-2 text-zinc-500 font-medium">Account</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Earned</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Invoiced</th>
                    <th className="text-right px-3 py-2 text-zinc-500 font-medium">Shortfall</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,14%)]">
                  {claim.rows
                    .filter((r) => r.missingFromAdwstats && r.earnedTotal > 0)
                    .sort((a, b) => b.earnedTotal - a.earnedTotal)
                    .slice(0, 150)
                    .map((row) => (
                      <tr key={row.key} className="hover:bg-[hsl(225,15%,12%)]">
                        <td className="px-3 py-2">
                          <div className="text-zinc-200 truncate max-w-[220px]">{row.partner || '—'}</div>
                          <div className="text-[10px] text-zinc-500 truncate max-w-[220px]">
                            {row.brands.slice(0, 3).join(', ') || '—'}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[10px] text-zinc-400">
                          {row.accountId || '—'}
                          {row.newToThisReport && <span className="text-amber-400/80"> · new to v2</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-400">{fmtEUR(row.earnedTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{fmtEUR(row.invoicedTotal)}</td>
                        <td className={`px-3 py-2 text-right tabular-nums ${row.underpaid < 0 ? 'text-red-400' : 'text-zinc-600'}`}>
                          {fmtEUR(row.underpaid)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------
const CARD_COLORS: Record<string, string> = {
  red: 'text-red-400 bg-red-500/10 border-red-500/20',
  amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
  zinc: 'text-zinc-300 bg-zinc-500/10 border-zinc-500/20',
};

const StatCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ReactNode;
  color: keyof typeof CARD_COLORS;
}> = ({ label, value, hint, icon, color }) => (
  <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
    <div className="flex items-center justify-between mb-2">
      <span className="text-[11px] text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className={`p-1.5 rounded-lg border ${CARD_COLORS[color]}`}>{icon}</span>
    </div>
    <p className="text-lg font-semibold text-white tabular-nums">{value}</p>
    {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
  </div>
);

const Explainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-[hsl(225,15%,9%)] border border-[hsl(225,15%,16%)] rounded-lg px-4 py-3">
    <p className="text-xs text-zinc-400 leading-relaxed">{children}</p>
  </div>
);

const BridgeRow: React.FC<{ step: ClaimBridgeStep }> = ({ step }) => {
  const tone: Record<ClaimBridgeStep['kind'], string> = {
    figure: 'text-zinc-200',
    subtotal: 'text-white font-semibold',
    addition: 'text-emerald-400',
    deduction: 'text-red-400',
    result: 'text-red-400 font-bold',
  };
  return (
    <div className={`px-4 py-3 ${step.kind === 'subtotal' || step.kind === 'result' ? 'bg-[hsl(225,15%,12%)]' : ''}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-xs ${step.kind === 'result' || step.kind === 'subtotal' ? 'text-white font-medium' : 'text-zinc-300'}`}>
              {step.label}
            </p>
            {step.theirFigure && (
              <span className="px-1.5 py-0.5 rounded border border-zinc-500/20 bg-zinc-500/10 text-zinc-400 text-[9px] uppercase tracking-wide">
                their figure
              </span>
            )}
          </div>
          {step.note && <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">{step.note}</p>}
        </div>
        <div className="text-right shrink-0">
          <p className={`text-sm tabular-nums ${tone[step.kind]}`}>
            {step.amount === null ? '—' : fmtEURExact(step.amount)}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Month-by-month view of every figure held for an account.
 *
 * Old recon, synced and Affiliation Cloud are shown for comparison even when
 * they lost to another source, so the drilldown is the complete evidence
 * picture. Only `Best` feeds the claim; a dot marks which column supplied it.
 */
const MonthlyComparison: React.FC<{ row: ClaimRow }> = ({ row }) => {
  const months = row.monthly;
  if (months.length === 0) return null;

  const has = {
    ftds: months.some((m) => m.ftds > 0),
    invoiced: months.some((m) => m.invoiced !== 0),
    oldRecon: months.some((m) => m.oldRecon !== 0),
    synced: months.some((m) => m.synced !== 0),
    ac: months.some((m) => m.ac !== 0),
    restated: months.some((m) => m.restated !== 0),
  };

  const cell = (value: number, isBest: boolean, tone: string) => (
    <td className={`py-1 px-2 text-right tabular-nums ${value ? tone : 'text-zinc-700'}`}>
      {value ? fmtEUR(value) : '—'}
      {isBest && value > 0 && <span className="text-white/70"> •</span>}
    </td>
  );

  const totals = months.reduce((acc, m) => ({
    invoiced: acc.invoiced + m.invoiced,
    oldRecon: acc.oldRecon + m.oldRecon,
    synced: acc.synced + m.synced,
    ac: acc.ac + m.ac,
    restated: acc.restated + m.restated,
    earned: acc.earned + m.earned,
    ftds: acc.ftds + m.ftds,
  }), { invoiced: 0, oldRecon: 0, synced: 0, ac: 0, restated: 0, earned: 0, ftds: 0 });

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <p className="text-[11px] text-zinc-500">
          Month by month, every figure available. Only <span className="text-white/80">Best</span> is claimed on;
          the others are shown for comparison.
        </p>
        <p className="text-[10px] text-zinc-600">• marks the source used that month</p>
      </div>
      <div className="overflow-x-auto border border-[hsl(225,15%,18%)] rounded-lg">
        <table className="w-full text-[10px]">
          <thead className="bg-[hsl(225,15%,10%)]">
            <tr>
              <th className="text-left py-1.5 px-2 text-zinc-500 font-medium">Month</th>
              {has.ftds && <th className="text-right py-1.5 px-2 text-zinc-500 font-medium">FTDs</th>}
              {has.invoiced && <th className="text-right py-1.5 px-2 text-zinc-400 font-medium">Invoiced</th>}
              {has.oldRecon && <th className="text-right py-1.5 px-2 text-zinc-400 font-medium">Old recon</th>}
              {has.synced && <th className="text-right py-1.5 px-2 text-emerald-400/70 font-medium">Synced −5%</th>}
              {has.ac && <th className="text-right py-1.5 px-2 text-indigo-400/70 font-medium">AC</th>}
              {has.restated && <th className="text-right py-1.5 px-2 text-amber-400/70 font-medium">Restated</th>}
              <th className="text-right py-1.5 px-2 text-white/70 font-medium">Best</th>
              <th className="text-right py-1.5 px-2 text-red-400/70 font-medium">Diff</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[hsl(225,15%,13%)]">
            {months.map((m) => (
              <tr key={m.month} className={m.unsupported ? 'bg-amber-500/[0.06]' : undefined}>
                <td className="py-1 px-2 text-zinc-400 whitespace-nowrap">{fmtMonth(m.month)}</td>
                {has.ftds && (
                  <td className="py-1 px-2 text-right tabular-nums text-zinc-500">
                    {m.ftds ? fmtInt(m.ftds) : '—'}
                  </td>
                )}
                {has.invoiced && cell(m.invoiced, false, 'text-zinc-300')}
                {has.oldRecon && cell(m.oldRecon, m.oldRecon === m.earned, 'text-zinc-300')}
                {has.synced && cell(m.synced, m.synced === m.earned, 'text-emerald-400')}
                {has.ac && cell(m.ac, m.ac === m.earned, 'text-indigo-400')}
                {has.restated && cell(m.restated, m.restated === m.earned, 'text-amber-400')}
                <td className="py-1 px-2 text-right tabular-nums text-white">
                  {m.earned ? fmtEUR(m.earned) : '—'}
                </td>
                <td className={`py-1 px-2 text-right tabular-nums ${
                  m.unsupported ? 'text-amber-400'
                    : m.underpaid < -1 ? 'text-red-400'
                      : m.underpaid > 1 ? 'text-amber-400/80' : 'text-zinc-700'
                }`}>
                  {m.unsupported
                    ? <span title="Invoiced with nothing to compare against">no data</span>
                    : Math.abs(m.underpaid) > 1 ? fmtEUR(m.underpaid) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-[hsl(225,15%,10%)] border-t border-[hsl(225,15%,18%)]">
            <tr className="font-medium">
              <td className="py-1.5 px-2 text-zinc-500">Total</td>
              {has.ftds && (
                <td className="py-1.5 px-2 text-right tabular-nums text-zinc-400">{fmtInt(totals.ftds)}</td>
              )}
              {has.invoiced && <td className="py-1.5 px-2 text-right tabular-nums text-zinc-300">{fmtEUR(totals.invoiced)}</td>}
              {has.oldRecon && <td className="py-1.5 px-2 text-right tabular-nums text-zinc-300">{fmtEUR(totals.oldRecon)}</td>}
              {has.synced && <td className="py-1.5 px-2 text-right tabular-nums text-emerald-400">{fmtEUR(totals.synced)}</td>}
              {has.ac && <td className="py-1.5 px-2 text-right tabular-nums text-indigo-400">{fmtEUR(totals.ac)}</td>}
              {has.restated && <td className="py-1.5 px-2 text-right tabular-nums text-amber-400">{fmtEUR(totals.restated)}</td>}
              <td className="py-1.5 px-2 text-right tabular-nums text-white">{fmtEUR(totals.earned)}</td>
              <td className={`py-1.5 px-2 text-right tabular-nums ${
                totals.invoiced - totals.earned < 0 ? 'text-red-400' : 'text-zinc-500'
              }`}>
                {fmtEUR(totals.invoiced - totals.earned)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {row.monthly.some((m) => m.unsupported) && (
        <p className="text-[10px] text-amber-500/70 mt-1.5">
          Highlighted months were invoiced with no earnings data on any source, so they cannot be judged
          either way.
        </p>
      )}
    </div>
  );
};

const ClaimDetail: React.FC<{ row: ClaimRow }> = ({ row }) => (
  <div className="space-y-4">
    <div>
      <p className="text-[11px] text-zinc-500 mb-2">
        Every earnings figure available for this account. The highest one is used.
      </p>
      <div className="flex flex-wrap gap-2">
        {row.candidates.map((c) => (
          <div
            key={c.source}
            className={`px-3 py-2 rounded-lg border text-[11px] ${
              c.source === row.chosenSource
                ? 'border-amber-500/40 bg-amber-500/5'
                : 'border-[hsl(225,15%,20%)] bg-[hsl(225,15%,11%)]'
            } ${c.available ? '' : 'opacity-40'}`}
          >
            <div className="text-zinc-500">{CLAIM_SOURCE_LABELS[c.source]}</div>
            <div className="text-zinc-100 tabular-nums">
              {c.available ? fmtEURExact(c.amount) : 'not available'}
            </div>
            {c.available && c.originalCurrency && c.originalAmount ? (
              <div className="text-[10px] text-zinc-600">
                {c.originalCurrency} {c.originalAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} original
              </div>
            ) : null}
            {c.available && c.marginApplied && (
              <div className="text-[10px] text-amber-500/70">less 5% margin</div>
            )}
          </div>
        ))}
      </div>
    </div>

    {row.earnedTail > 0 && (
      <div className="text-[11px] text-zinc-500">
        Dec 2024 onward adds <span className="text-purple-400 tabular-nums">{fmtEURExact(row.earnedTail)}</span> from
        their own monthly columns
        {row.earnedTailFromAc > 0 && (
          <> (Affiliation Cloud shows <span className="tabular-nums">{fmtEURExact(row.earnedTailFromAc)}</span> for the same months)</>
        )}.
      </div>
    )}

    <MonthlyComparison row={row} />

    {row.invoiceEntries.length > 0 && (
      <div>
        <p className="text-[11px] text-zinc-500 mb-2">
          Invoice lines Raketech raised against this account
        </p>
        <div className="space-y-2">
          {row.invoiceEntries.map((entry, i) => (
            <div key={`${entry.label}|${i}`} className="border border-[hsl(225,15%,18%)] rounded-lg p-3">
              <div className="flex items-start justify-between gap-3 mb-2">
                <span className="text-[11px] text-amber-400/90 break-all">{entry.label}</span>
                <span className="text-[11px] text-zinc-300 tabular-nums shrink-0">
                  {fmtEURExact(entry.revenue)}
                  {entry.ftds > 0 && (
                    <span className="text-zinc-600"> · {fmtInt(entry.ftds)} FTDs</span>
                  )}
                </span>
              </div>
              {entry.monthly.length > 0 && (
                <table className="w-full max-w-md text-[10px]">
                  <thead>
                    <tr className="text-zinc-600">
                      <th className="text-left py-0.5 pr-2 font-medium">Month</th>
                      <th className="text-right py-0.5 px-2 font-medium">FTDs</th>
                      <th className="text-right py-0.5 pl-2 font-medium">Invoiced</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                    {entry.monthly.map((m) => (
                      <tr key={m.month} className="text-zinc-400">
                        <td className="py-0.5 pr-2">{fmtMonth(m.month)}</td>
                        <td className="py-0.5 px-2 text-right tabular-nums">{m.ftds ? fmtInt(m.ftds) : '—'}</td>
                        <td className="py-0.5 pl-2 text-right tabular-nums">{fmtEUR(m.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    )}

    {row.offers.length > 0 && (
      <div>
        <p className="text-[11px] text-zinc-500 mb-2">
          Offers Raketech lists for this account in the new report
        </p>
        <div className="flex flex-wrap gap-1.5">
          {row.offers.map((o) => (
            <span
              key={`${o.offer}|${o.brand}`}
              className="px-2 py-1 rounded border border-[hsl(225,15%,20%)] bg-[hsl(225,15%,11%)] text-[10px] text-zinc-400"
            >
              {o.brand || o.offer}
              {o.pa && <span className="text-amber-500/70"> · PA</span>}
              <span className="text-zinc-600"> {fmtEUR(o.totalEarnings)}</span>
            </span>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default RaketechFinalReportV2;
